// src/components/fairplay/FairPlay.jsx
// What players see of fair play (backend fairplay.py / fairplay_routes.py): a neutral "under review" or
// "excluded" mark on duels and accounts, the watermarked result card rendered by the server, and a one-time
// appeal. Never the reasons, signals or thresholds.
import React, { createContext, useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { duelFetch } from '../../pages/duelIdentity';

const API = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
export const FP_LABEL = { review: 'Suspicious activity – under review', excluded: 'Excluded – fair play violation' };
export const FP_MESSAGE = {
  review: 'This result is under fair play review and doesn’t count on the leaderboard for now.',
  excluded: 'This result was excluded for a fair play violation and doesn’t count on the leaderboard.',
};
const TONE = { review: { bg: '#a8690a', fg: '#fff' }, excluded: { bg: '#c0392b', fg: '#fff' } };

export function useDuelFairPlay(duelId) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!duelId) return undefined;
    let off = false;
    duelFetch(`/api/fairplay/duels/${duelId}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (!off && j) setStatus(j.status); }).catch(() => {});
    return () => { off = true; };
  }, [duelId]);
  return status;
}

export function usePlayerFairPlay(username) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!username) return undefined;
    let off = false;
    fetch(`${API}/api/fairplay/players/${encodeURIComponent(username)}`).then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!off && j) setStatus(j.status); }).catch(() => {});
    return () => { off = true; };
  }, [username]);
  return status;
}

// Badge next to a player's name on their profile (everyone sees it): stays until every flag on the account
// and its duels is resolved.
export function ProfileFairPlayBadge({ username }) {
  const status = usePlayerFairPlay(username);
  if (!status) return null;
  const t = TONE[status];
  return (
    <Box component="span" role="status" title={status === 'review'
      ? 'This account’s results are under fair play review and don’t count on the leaderboard until the review is done.'
      : 'This account was found in breach of fair play. Its results don’t count on the leaderboard.'} sx={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 12.5, fontWeight: 700, px: '10px', py: '4px', borderRadius: 999,
      bgcolor: t.bg, color: t.fg, maxWidth: '100%', boxSizing: 'border-box', letterSpacing: '.01em',
      backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,.10) 0 8px, transparent 8px 16px)',
    }}>
      <Box component="span" aria-hidden sx={{ fontSize: 13 }}>⚑</Box>
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{status === 'review' ? 'Flagged · suspicious activity under review' : 'Excluded · fair play violation'}</Box>
      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{status === 'review' ? 'Flagged · under review' : 'Excluded · fair play'}</Box>
    </Box>
  );
}

// {duel_id: 'review' | 'excluded'} for a list of duels (history rows).
export function useDuelStatuses(ids) {
  const [map, setMap] = useState({});
  const key = [...new Set((ids || []).filter(Boolean))].sort().join(',');
  useEffect(() => {
    if (!key) return undefined;
    let off = false;
    duelFetch('/api/fairplay/duels/status', 'POST', { ids: key.split(',').map(Number) })
      .then((r) => (r.ok ? r.json() : {})).then((j) => { if (!off) setMap(j || {}); }).catch(() => {});
    return () => { off = true; };
  }, [key]);
  return map;
}

export const FairPlayContext = createContext({});
export const useFairPlayMap = () => useContext(FairPlayContext);

export function FairPlayPill({ status, sx }) {
  if (!status) return null;
  const t = TONE[status];
  return (
    <Box component="span" title={FP_MESSAGE[status]} sx={{
      display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, px: '7px', py: '2px', borderRadius: 999,
      bgcolor: t.bg, color: t.fg, whiteSpace: 'nowrap', letterSpacing: '.01em', ...sx,
    }}>{status === 'review' ? 'Under review' : 'Excluded'}</Box>
  );
}

// A strip pinned to the top of a duel page (result screen, replay, invite, spectate).
export function FairPlayStrip({ duelId, status: given }) {
  const fetched = useDuelFairPlay(given ? null : duelId);
  const status = given || fetched;
  if (!status) return null;
  const t = TONE[status];
  return (
    <Box role="status" sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000, bgcolor: t.bg, color: t.fg, px: 2, py: '7px',
      textAlign: 'center', fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', boxShadow: '0 2px 10px rgba(0,0,0,.3)',
      backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,.08) 0 12px, transparent 12px 24px)',
    }}>
      {FP_LABEL[status].toUpperCase()} <Box component="span" sx={{ fontWeight: 500, opacity: 0.9, display: { xs: 'none', sm: 'inline' } }}>· {FP_MESSAGE[status]}</Box>
    </Box>
  );
}

// The result card as the server renders it (watermark baked in). Download / share use the same image.
export function ServerCard({ duelId, width = '100%' }) {
  const url = `${API}/api/fairplay/duels/${duelId}/card.png`;
  const [busy, setBusy] = useState(false);
  const get = async () => {
    const r = await fetch(url);
    return r.blob();
  };
  const download = async () => {
    setBusy(true);
    try {
      const b = await get();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = `nummius_duel_${duelId}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch { /* ignore */ }
    setBusy(false);
  };
  const share = async () => {
    try {
      const file = new File([await get()], `nummius_duel_${duelId}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: 'Nummius Arena Duel' });
      else download();
    } catch { /* cancelled */ }
  };
  const btn = { border: 0, borderRadius: '10px', px: 2, height: 40, fontWeight: 700, fontSize: 14, cursor: 'pointer', font: 'inherit' };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
      <Box component="img" src={url} alt="Duel result card" sx={{ width, maxWidth: 600, borderRadius: '12px', display: 'block' }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box component="button" type="button" onClick={download} disabled={busy} sx={{ ...btn, bgcolor: '#e9edf1', color: '#111' }}>Download</Box>
        {typeof navigator !== 'undefined' && navigator.share && <Box component="button" type="button" onClick={share} sx={{ ...btn, bgcolor: '#e9edf1', color: '#111' }}>Share</Box>}
      </Box>
    </Box>
  );
}

// One short appeal per flag.
export function AppealButton({ targetType, targetId, appealed, onSent, sx }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [state, setState] = useState(appealed ? 'sent' : null);
  const [err, setErr] = useState(null);
  if (state === 'sent') return <Box component="span" sx={{ fontSize: 12.5, opacity: 0.8, ...sx }}>Appeal sent</Box>;
  const send = async () => {
    setState('sending'); setErr(null);
    try {
      const r = await duelFetch('/api/fairplay/appeals', 'POST', { target_type: targetType, target_id: targetId, message: msg });
      const j = await r.json().catch(() => null);
      if (!r.ok) { setErr(j?.error || 'Could not send it.'); setState(null); return; }
      setState('sent'); onSent?.();
    } catch { setErr('Network error.'); setState(null); }
  };
  if (!open) {
    return <Box component="button" type="button" onClick={() => setOpen(true)} sx={{ border: 0, bgcolor: 'transparent', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', fontSize: 13, p: 0, ...sx }}>Appeal</Box>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', ...sx }}>
      <Box component="textarea" rows={3} maxLength={1000} value={msg} onChange={(e) => setMsg(e.target.value)} aria-label="Your appeal"
        placeholder="Tell us briefly why this result is fair (one appeal per review)." sx={{ font: 'inherit', fontSize: 14, p: 1, borderRadius: '8px', border: '1px solid rgba(127,127,127,.4)', bgcolor: 'transparent', color: 'inherit', resize: 'vertical' }} />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box component="button" type="button" disabled={state === 'sending' || msg.trim().length < 5} onClick={send}
          sx={{ border: 0, borderRadius: '8px', px: 1.5, height: 34, fontWeight: 700, cursor: 'pointer', bgcolor: '#16857a', color: '#fff', font: 'inherit', fontSize: 13, '&:disabled': { opacity: 0.5 } }}>
          {state === 'sending' ? 'Sending…' : 'Send appeal'}
        </Box>
        <Box component="button" type="button" onClick={() => setOpen(false)} sx={{ border: 0, bgcolor: 'transparent', color: 'inherit', cursor: 'pointer', font: 'inherit', fontSize: 13 }}>Cancel</Box>
        {err && <Box component="span" role="alert" sx={{ fontSize: 12.5, color: '#d23f37' }}>{err}</Box>}
      </Box>
    </Box>
  );
}

// Account notice on a profile: own (with flagged duels + appeals) or someone else's (neutral line only).
export function AccountFairPlayNotice({ username, own }) {
  const [me, setMe] = useState(null);
  const theirs = usePlayerFairPlay(own ? null : username);
  useEffect(() => {
    if (!own) return undefined;
    let off = false;
    duelFetch('/api/fairplay/me').then((r) => (r.ok ? r.json() : null)).then((j) => { if (!off) setMe(j); }).catch(() => {});
    return () => { off = true; };
  }, [own]);
  const status = own ? me?.status : theirs;
  const flags = own ? (me?.flags || []) : [];
  if (!status && !flags.length) return null;
  const t = TONE[status || 'review'];
  return (
    <Box role="status" sx={{ borderRadius: '14px', p: '14px 16px', bgcolor: t.bg, color: t.fg, display: 'flex', flexDirection: 'column', gap: 1,
      backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,.07) 0 12px, transparent 12px 24px)' }}>
      <Box sx={{ fontWeight: 800, letterSpacing: '.02em' }}>{FP_LABEL[status || 'review'].toUpperCase()}</Box>
      <Box sx={{ fontSize: 14 }}>
        {status ? (own ? 'Your account is under fair play review. Results don’t count on the leaderboard until the review is done.'
          : 'This account’s results are under fair play review.')
          : 'Some of your duel results are under fair play review and don’t count on the leaderboard for now.'}
      </Box>
      {own && status && <AppealButton targetType="account" targetId={me?.user_id} appealed={me?.account_appealed} />}
      {own && flags.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13.5, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {flags.slice(0, 10).map((f) => (
            <li key={f.duel_id}>
              Duel #{f.duel_id} · {f.status === 'review' ? 'under review' : 'excluded'}{' '}
              · <AppealButton targetType="duel" targetId={f.duel_id} appealed={f.appealed} />
            </li>
          ))}
        </Box>
      )}
    </Box>
  );
}

// Same strip on routes that only know the invite code (spectate, peekabu).
export function FairPlayStripByCode({ code }) {
  const [id, setId] = useState(null);
  useEffect(() => {
    let off = false;
    fetch(`${API}/api/duels/${encodeURIComponent(code)}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (!off && j) setId(j.duel_id); }).catch(() => {});
    return () => { off = true; };
  }, [code]);
  return id ? <FairPlayStrip duelId={id} /> : null;
}

// Probation (guests, new accounts, restricted): the contract cap for this duel, enforced by the server too.
export function useProbation(duelId) {
  const [pb, setPb] = useState(null);
  useEffect(() => {
    if (!duelId) return undefined;
    let off = false;
    duelFetch(`/api/fairplay/probation?duel_id=${duelId}`).then((r) => (r.ok ? r.json() : null)).then((j) => { if (!off) setPb(j); }).catch(() => {});
    return () => { off = true; };
  }, [duelId]);
  return pb && pb.active && pb.max ? pb : null;
}

export function probationText(pb) {
  if (!pb) return null;
  if (pb.restricted) return `This account can trade at most ${pb.max} ${pb.unit} per position.`;
  if (pb.guest) return `Guests: max ${pb.max} ${pb.unit} per position. Sign up and play to lift it.`;
  const left = Math.max(0, pb.duels_needed - pb.duels_done);
  return `New account: max ${pb.max} ${pb.unit} per position · ${left} more finished duel${left === 1 ? '' : 's'} to lift it.`;
}
